import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

// Only this application-owned script may run in the opaque-origin frame. Email
// scripts are removed and cannot use its per-document CSP nonce.
const LAYOUT_SCRIPT = `(() => {
  const channel = document.currentScript.dataset.channel;
  let width = 0, queued = false;
  const measure = () => {
    queued = false;
    const available = document.documentElement.clientWidth;
    if (available && available !== width) {
      width = available;
      document.body.style.zoom = '1';
      const natural = Math.max(document.body.scrollWidth, document.documentElement.scrollWidth);
      document.body.style.zoom = String(Math.min(1, available / Math.max(natural, 1)));
    }
    const height = Math.min(30000, Math.max(80, Math.ceil(document.body.getBoundingClientRect().height) + 8));
    parent.postMessage({ type: 'leadmarka-email-size', channel, height }, '*');
  };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(measure); } };
  new ResizeObserver(schedule).observe(document.body);
  addEventListener('resize', schedule);
  addEventListener('load', schedule, true);
  addEventListener('message', event => {
    if (event.source === parent && event.data?.type === 'leadmarka-email-measure' && event.data.channel === channel) schedule();
  });
  schedule();
})();`;

export function emailDocument(html, channel) {
  const clean = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true, USE_PROFILES: { html: true }, ADD_TAGS: ['style'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'video', 'audio', 'source', 'track', 'base', 'meta', 'link'],
    FORBID_ATTR: ['srcset', 'ping', 'autofocus'],
  });
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  doc.querySelectorAll('a').forEach(link => {
    const href = link.getAttribute('href') || '';
    if (!/^(https?:\/\/|mailto:)/i.test(href)) link.removeAttribute('href');
    link.setAttribute('target', '_blank'); link.setAttribute('rel', 'noopener noreferrer');
  });
  doc.querySelectorAll('img').forEach(img => {
    let src = img.getAttribute('src') || '';
    if (src.startsWith('//')) src = `https:${src}`;
    if (!/^https?:\/\//i.test(src) && !/^data:image\/(png|jpeg|gif|webp|avif);base64,[a-z0-9+/=\s]+$/i.test(src)) img.removeAttribute('src');
    else img.setAttribute('src', src);
    img.setAttribute('referrerpolicy', 'no-referrer');
  });
  const policy = doc.createElement('meta'); policy.httpEquiv = 'Content-Security-Policy';
  policy.content = `default-src 'none'; script-src 'nonce-${channel}'; style-src 'unsafe-inline'; img-src https: data:; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; form-action 'none'; base-uri 'none'; upgrade-insecure-requests`;
  doc.head.prepend(policy);
  const referrer = doc.createElement('meta'); referrer.name = 'referrer'; referrer.content = 'no-referrer'; doc.head.append(referrer);
  const viewport = doc.createElement('meta'); viewport.name = 'viewport'; viewport.content = 'width=device-width, initial-scale=1'; doc.head.append(viewport);
  const style = doc.createElement('style');
  style.textContent = `html,body{margin:0!important;padding:0!important;min-width:0!important}body{font-family:Arial,sans-serif;font-size:14px;line-height:1.5;overflow-wrap:anywhere}img{max-width:100%!important;height:auto}table{max-width:100%!important}a{overflow-wrap:anywhere}pre{white-space:pre-wrap}`;
  doc.head.append(style);
  const script = doc.createElement('script'); script.setAttribute('nonce', channel); script.dataset.channel = channel; script.textContent = LAYOUT_SCRIPT;
  doc.body.append(script);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

export default function HtmlEmail({ html }) {
  const frame = useRef(null);
  const [height, setHeight] = useState(320);
  const rendered = useMemo(() => {
    const channel = Array.from(crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
    return { channel, document: emailDocument(html, channel) };
  }, [html]);
  useEffect(() => {
    setHeight(320);
    const resize = event => {
      if (event.source !== frame.current?.contentWindow || event.data?.type !== 'leadmarka-email-size' || event.data.channel !== rendered.channel) return;
      const value = event.data.height;
      if (typeof value === 'number' && Number.isFinite(value)) setHeight(Math.max(80, Math.min(30000, value)));
    };
    window.addEventListener('message', resize);
    frame.current?.contentWindow?.postMessage({ type: 'leadmarka-email-measure', channel: rendered.channel }, '*');
    return () => window.removeEventListener('message', resize);
  }, [rendered]);
  return <iframe ref={frame} title="Email content" srcDoc={rendered.document}
    onLoad={() => frame.current?.contentWindow?.postMessage({ type: 'leadmarka-email-measure', channel: rendered.channel }, '*')}
    sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer"
    className="block w-full border-0 bg-white" style={{ height }} />;
}
